import { faPen, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "@hookstate/core/dist";
import React, { useCallback, useEffect } from "react";
import BootstrapTable, { TableChangeHandler } from "react-bootstrap-table-next";
import filterFactory from "react-bootstrap-table2-filter";
import { Helmet } from "react-helmet-async";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import CategoryRepository from "../../../../repositories/Brand/CategoryRepository";
import SkuChannelRepository from "../../../../repositories/Brand/SkuChannelRepository";
import StokProductRepository from "../../../../repositories/Brand/StockProductRepository";
import StockProductRepository from "../../../../repositories/Brand/StockProductRepository";
import VuiSelect from "../../../../vodea/@vodea-ui/components/Forms/VuiSelect";
import VuiUploadImage from "../../../../vodea/@vodea-ui/components/Forms/VuiUploadImage";
import VuiBreadcrumb from "../../../../vodea/@vodea-ui/components/VuiBreadcrumb";
import { RootState } from "../../../../stores";

import { VuiButton } from "../../../../vodea/@vodea-ui/components/VuiButton";
import { InformationInputs, informationSchema } from "./validation";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import useRoleService from "../../../../vodea/utilities/hooks/useRoleService";
import NumberFormat from "react-number-format";
import _ from "lodash";
import { $clone } from "../../../../vodea/utilities";
import useIsMounted from "../../../../vodea/utilities/hooks/useIsMounted";
import VuiDataTable from "../../../../vodea/@vodea-ui/components/VuiDataTable";
import {
  getChannelConfig,
  showToast,
} from "../../../../vodea/utilities/helpers/global";
import ModalUpdateStock from "../ModalUpdateStock";
import { AxiosError, AxiosResponse } from "axios";
import { IApiResource } from "../../../../interfaces/ApiResource";
import NumberTextField from "../../../../components/NumberTextField";

const StokForm: React.FC<any> = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { id } = useParams();
  const { isAdministrator, isKeyAccount } = useRoleService();
  const [data, setData] = React.useState<any[]>([]);
  const [showUpdateStock, setShowUpdateStock] = React.useState<boolean>(false);

  const {
    register: informationRegister,
    handleSubmit: informationHandleSubmit,
    errors: informationErrors,
    control: informationControl,
    watch: informationWatch,
    setValue: informationSetValue,
  } = useForm<InformationInputs>({
    mode: "onChange",
    resolver: yupResolver(informationSchema),
  });

  const {
    fields: pictureFields,
    append: pictureAppend,
    remove: pictureRemove,
  } = useFieldArray({
    control: informationControl,
    name: "photo_ids",
  });

  const pictures: any = informationWatch("photo_ids", []);

  const renderItemPicture = (item: any, index: number) => (
    <div key={item.id}>
      <Controller
        render={({ value, onChange, name }) => (
          <VuiUploadImage
            key={index}
            className={"picture-file"}
            defaultValue={value}
            onChange={(val) => {
              if (val) {
                if (index === pictureFields.length - 1) {
                  if (index < 4) {
                    pictureAppend({ picture: null as any });
                  }
                }
                onChange(val);
              } else {
                onChange(null);
              }
            }}
            onDelete={() => {
              pictureRemove(index);
              if (pictures[pictureFields.length - 1]?.picture !== null) {
                pictureAppend({ picture: null as any });
              }
            }}
          />
        )}
        name={`photo_ids[${index}].picture`}
        control={informationControl}
        defaultValue={item.picture}
      />
    </div>
  );

  const handleOpenUpdateStock = () => setShowUpdateStock(true);
  const handleCloseUpdateStock = () => setShowUpdateStock(false);
  const [show, setShow] = React.useState(false);
  const handleClose = () => setShow(false);
  const { brand } = useSelector((state: RootState) => state.brand);
  const [categoryList, setCategoryOption] = React.useState<any>([]);
  const [skuProductList, setSkuProductList] = React.useState<any>([]);
  const [selectedSkuProduct, setSkuProduct] = React.useState<any>({});
  const [tableData, setTableData] = React.useState<any>([]);
  const [warningForm, setWarningForm] = React.useState<any>(0);
  const [fullDataPhotos, setPhotoData] = React.useState<any>([]);

  const [loadCategory, setLoadingCategory] = React.useState(false);
  const [loadSKU, setLoadingSKU] = React.useState(false);

  const tableVendorConfig = useState({
    search: "",
    page: 1,
    per_page: 10,
    sorted_by: "desc",
    order_by: "quantity",
    total: 0,
    loading: false,
  });

  const tableVendorData = useState<any[]>([]);

  const updateStockVendor = useCallback(
    async (productData, rowIndex) => {
      const productStockData = {
        product_stocks: [productData],
      };

      await StockProductRepository.updateStockVendor(
        brand?.id,
        id,
        productStockData
      )
        .then((response: AxiosResponse<IApiResource<any>>) => {
          showToast(t("response.successUpdateData"), "success");
          if (isMounted.current) {
            const cloneData = [...data];
            cloneData[rowIndex].quantity = productData.quantity;
            cloneData[rowIndex].isEdit = false;
            setData(cloneData);
          }
        })
        .catch((error: AxiosError) => {
          showToast(error.message, "error");
        });
    },
    [data]
  );

  const onOutFocusQuantityField = (index: number) => {
    const cloneData = [...data];
    cloneData[index].isEdit = false;
    setData(cloneData);
  };

  const tableVendorColumns = [
    {
      dataField: "vendor_name",
      text: t("stock.index.table.nama_vendor"),
      sort: true,
      classes: "valign-middle",
    },
    {
      dataField: "quantity",
      text: t("stock.index.table.quantity"),
      sort: true,
      classes: "valign-middle",
      formatter: (cell: any, row: any, rowIndex: number) => {
        return data[rowIndex].isEdit ? (
          <NumberTextField
            defaultValue={data[rowIndex]?.quantity}
            autoFocus
            decimalScale={0}
            onValueChange={(values) => values.value}
            onKeyDown={(e: any) => {
              if (e.key === "Enter") {
                if (data[rowIndex].quantity != e.target.value) {
                  updateStockVendor(
                    {
                      vendor_id: row.vendor_id,
                      quantity: parseFloat(e.target.value.replace(".", "")),
                    },
                    rowIndex
                  );
                } else {
                  onOutFocusQuantityField(rowIndex);
                }
              }
            }}
            onBlur={(e) => {
              if (data[rowIndex].quantity != e.target.value) {
                updateStockVendor(
                  {
                    vendor_id: row.vendor_id,
                    quantity: parseFloat(e.target.value.replace(".", "")),
                  },
                  rowIndex
                );
              } else {
                onOutFocusQuantityField(rowIndex);
              }
            }}
          />
        ) : (
          <>
            <NumberTextField
              decimalScale={0}
              displayType={"text"}
              isForm={false}
              defaultValue={data[rowIndex]?.quantity}
            />

            <FontAwesomeIcon
              className={"icon icon-prefix"}
              style={{ cursor: "pointer", marginLeft: 16 }}
              icon={faPen}
              onClick={() => {
                const cloneData = [...data];
                cloneData.map((data) => (data.isEdit = false));
                cloneData[rowIndex].isEdit = true;
                setData(cloneData);
              }}
            />
          </>
        );
      },
    },
  ];

  const changeTableLoading = (val: boolean) => {
    if (isMounted.current) {
      tableVendorConfig.loading.set(val);
    }
  };

  const getVendorTableData = (reset: boolean = false) => {
    if (reset) tableVendorConfig.page.set(1);

    changeTableLoading(true);

    const conf = _.omit($clone(tableVendorConfig.value), "loading");

    let params: any = {};
    Object.keys(conf).forEach((prop) => {
      if (conf[prop]) {
        params[prop] = conf[prop];
      }
    });

    tableVendorData.set([]);

    StokProductRepository.allStockVendor(brand?.id, {
      ...params,
      product: id,
    })
      .then((resp: any) => {
        if (isMounted.current) {
          setData(
            resp.data.data.map((data: any) => {
              return {
                ...data,
                isEdit: false,
              };
            })
          );
          tableVendorData.set(resp.data.data);
          tableVendorConfig.total.set(resp.data.meta.total);
        }
        changeTableLoading(false);
      })
      .catch((err: any) => {
        changeTableLoading(false);
      });
  };

  useEffect(() => {
    if (id) {
      getVendorTableData();
    }
  }, [id]);

  const onVendorTableChange: TableChangeHandler<any> = (
    type,
    { page, sizePerPage, sortOrder, sortField }
  ) => {
    tableVendorConfig.page.set(page);
    tableVendorConfig.per_page.set(sizePerPage);
    if (sortOrder) tableVendorConfig.sorted_by.set(sortOrder);
    if (sortField) tableVendorConfig.order_by.set(sortField);

    getVendorTableData();
  };

  const fetchAllCategory = () => {
    CategoryRepository.all(brand.id).then((res: any) => {
      setLoadingCategory(true);
      const categoryList: any = [];
      for (var i = 0; i < res.data.data.length; i++) {
        if (res.data.data[i] != null) {
          if (res.data.data[i].brand_id == brand.id) {
            const data = {
              name: res.data.data[i].name,
              id: res.data.data[i].id,
            };
            categoryList.push(data);
          }
        }
      }

      setCategoryOption(categoryList);
    });
  };

  const fetchAllSkuProduk = () => {
    SkuChannelRepository.all(
      brand.id,
      id
        ? {
            for: "product_sku_channel",
            with: ["external", "parentExternal"],
            except_product: id,
          }
        : {
            for: "product_sku_channel",
            with: ["external", "parentExternal"],
          }
    ).then((res: any) => {
      const dataSkuProduct = [];
      setLoadingSKU(true);
      for (var i = 0; i < res.data.data.length; i++) {
        if (res.data.data[i] != null) {
          if (res.data.data[i].brand_id == brand.id) {
            dataSkuProduct.push(res.data.data[i]);
          }
        }
      }
      setSkuProductList(dataSkuProduct);
    });
  };

  useEffect(() => {
    (async () => {
      await Promise.all([fetchAllCategory(), fetchAllSkuProduk()]).then(() => {
        if (id) {
          StokProductRepository.show(brand.id, id, {
            with: [
              "photos",
              "skuChannels.external",
              "skuChannels.parentExternal",
            ],
          }).then((response: any) => {
            const sku_channels = response.data.data.sku_channels;
            const arraySkuChannels: any = [];
            informationSetValue("sku", response.data.data.sku);
            informationSetValue("name", response.data.data.name);
            informationSetValue(
              "vendor_price",
              response.data.data.vendor_price
            );
            sku_channels.map((sku_channel: any) => {
              const initialSkuProduct = {
                id: sku_channel.id,
                product: {
                  name: sku_channel.name,
                  image: sku_channel.photo_url,
                },
                external: sku_channel.external,
                parent_external: sku_channel.parent_external,
                sku_product: sku_channel.sku,
                harga: "Default Price",
                toko: sku_channel.channel_id == 1 ? "Tokopedia" : "Shopee",
              };
              arraySkuChannels.push(initialSkuProduct);
            });
            setTableData(arraySkuChannels);
            CategoryRepository.show(
              brand.id,
              response.data.data.category_id
            ).then((responseCategory: any) => {
              informationSetValue("category_id", {
                id: responseCategory.data.data.id,
                name: responseCategory.data.data.name,
              });
            });

            const photoData = response.data.data.photos;
            if (photoData.length == 0) {
              pictureAppend({
                picture: null,
              });
            }
            setPhotoData(photoData);
          });
        }
      });
    })();
  }, []);

  const defaultImageSrc = (ev: any) => {
    ev.target.src =
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAANkAAADoCAMAAABVRrFMAAAAflBMVEX///8AAAC0tLREREShoaFQUFBpaWnMzMz39/fz8/P7+/vn5+fw8PCwsLDCwsLY2NhfX1+VlZXX19fj4+O6uro1NTWLi4slJSXJyclxcXEwMDBlZWVAQEDe3t6pqalJSUl5eXmBgYEXFxeHh4dYWFiSkpIrKyseHh4QEBA6OjrMVwouAAAKpklEQVR4nO2d53rrIAyGkzbTGc1omo6kx0mzev83eJqNQAJhg8F5/P1t8MNbYxCSELXaTcvGx+ehXkIdZh/f0xqpp3noDubTbIhzTWehe5Zf/3oI2DB0r9zoXQF7Dd0lV5JHZDt0h9zpBYD1Q3fHpToiWTN0b1zq6zHH4lHCeNyE7otb3V/aQ31lR92+tAdZyu66zfxvoXviWtsr2UfonrhW6yHn/KNGV7Ln0D1xrSZJ9jt4KpHGfLK5Yi/HrR6brBGym1n09bBkrYqsIotGFVlFFo8qsoosHlVkFVk8qshiIetO2u32S8L4ZZnIJuObd3feamBRMVHlIWvIwddnIpp5UVnIvuuIdm1Ni3KQ9RYY2J8+6DalINN44vfk51YGsgEN9qcl0aoEZOgnZkaLn8wcFMIHZPRkikdU1Q5tGD3ZzkxWT7GGsZOp/nlME6Rl5GRdFlh9gTSNnGzLI6sjxkjkZEyw+kZtGjcZPw2gq7SNmyxlk70qbeMm40z5Z6VK26jJmDPjUc9K46jJJnyyutI4ajKbzNGO3PhhyPpy46jJ9BszKCV9PWoym3embGUehqxc39njzo0W69mv0jhqstqeTaa65+Im4ydZPilt4ybjTyHKchY52ePuz9jZzOqZntjJmLn1M6Rp5GRMRwgWk4mdjDXxoxGZ6MmWZrA12jB6stqTkaykfv1arWEAwxzEtVKQGdCoiG4ZyLQDkgoMloOstqTccyM6M6QcZMSI3OvyJspCVuso8aadat+LKg3Zn9rp5607z1vyA7uoTGR/6kzfh6/D957i9VBVMjILVWQVWTyqyCqyeFSRVWTxqCKryCj1VMe7ZxVClpyc2B+mswNuVQRZ71qVTr9VdKwCyARfqJod5U/+yV7EpxC+QR/yTiZF0YubSHyTvUuPmXMOWDmRZzI19RKJTvqRXzLMuZu66rpBXsnwvKlvh93XyCcZFWlAgspsJezv1CMZHULJbIyMd/X6IeU190emSQtYMxyhiHrrS3vWgu+NbEWD4WcijOrc23PMNF9khnx0zSlGUj9Ce/3h1ZM8kcmPVTS2BoP5POZZyA+ZXGsKkbVxLCUZ6I7l+iNjlet8MT8HSDZnTO19kDHLCFoax0pkUFPS2xMZdfZZFn6IkZSa66hf15yTJfxK4SPz0wQhldW1b901WcemtPvKhgxpf1DPZnkj6/Mzf4+yMI6nWPu9xphxS9a3vWHAOHffhB+ymxWUD8I4+yyLbRwT9rWaze6DDB0wBu25uxIqDbxJNXBI9lLPIq5xTK4lFJo7MpujHqJ4xnFCP+ALb+GMLHtpd5ZxrPuEW2gLV2R5yoQztiSKdw8o9UiW78YLUwpVzZS9+eaNzOZwHyadLXGWYSeLjGgnZKaqK0Z9Gsl+DU9Qu+eCjFfpQiujcWx8gmKoOSDj1oPQ6kcPxrBuZK9PfrKfuhMNtGScNUXyP+QmWznhqhuMY9aXDJ+Ql8zh1Qk643jFegJAy0lm9L5ZSGcc/+M9QlwY85G5vVPmH022Nrc+SfD65CJzfdcFaRzzL6+5D+k8ZO6vuqCmKQtz++b1yU6WmMyCLCKMYwvrbX211DKTJZ91H8KN45XFE64x/qxkXV8XK6LGsdVM9ZnkIetzJytrYcdsGceq14LX7DcHWQYnFVuIcdyhfrtIx6+Tu6e4M50MtqP92X+QicwnGLaNRF0smwYVjen1spIxjsvmkhKrVafGD7OHQd6rMsiyed9sJBvHUjB/M2Q5KaewmZmsiPsHpSALmBpTQ9xM1Kuw5hrJtE4kV5KMY2GFGVsmXLzfOExkBV3LChy/96I1aYZEkvaORWY+kO5IqdC364w1ypj7880gy+t9s5DgsbmMk+yput2Wicx0zt6p7vP6aY7bZMtouv9zdGQOvG82us2Bx+wSvQPIrP7iVgdRJSv8Hsyrcby3Tx5BdBveMtl38Rd8XuOZ9V+zd9xCMpk3416jlvTfVtVvP721Zqew/2H+Zx8PGVdcuHcFZBAWY7nr/Qcr1NBsGEI7UZBpZvrlim41a+hyYOIgozzHA9OWfkO7nCMhQx1135y0k39UCmQcZJhzlW26PuO7gjjI1JmuZ+O+RWegKMhUE9g2vIrMkzGQqS6BkfUzVLdzBGSp3KepbcLaUYovLDzZXu5SRgfMcxIbmXy+MLOfYteNi0yOyefIElp3oyKTxpAmYe23la7eVqlmdtnFRCYZ+FTu5GYgLMfJpEGsds/xkEkp73gSYHOouhC632j4S8inC0wmGX1YmGlDubLesb7f3Q1hyaRELNS1dKCddJhpeRu1Yclg0IIKkWh2YWqeyjwKsgPsJpVDPNd46lSf79XOCkoGjT3af4sGSC9Sl4l+BGTgXZARz7o2OUZdKEbhyeA5eW2WoS4xUkFbBicDfh1NPvtRuqR42YQeBScDg9EUTNAdiJL3qdPAZPBogfHnunx/KQyfBiYD0Qlp74L5TjU+ZHkoJ2HJgMtJOuabYOEFTWRtoP4yIJnYM2nKH+OZ+5rT1dA+bgYlA8dcJFMC+XZOouv7SCZkNyQZ+Gyg/Xf2H2J7MDqVAgaRhiHJwP8fnhu9WPdYZiVpHcNI7U9IMtFpATO8rqHmBJkhD5R1DB8xC0kmOkDgV3Ibp13E8UierYEknXBk4HgktBnvrmwsZZo6Vyk/IxgZsEDgBCL8AduMEgWnYKLYMBwZ8DOCACBIWcK8dPiJT/h+G+HIgBkI/gJ9q5hnFbeOpZ8EIxP30wn5lxqeA7bFyMAikcZBBgeSHHTC9jeYdQxcel/hyERLH65FSjgN220jZZXANNSMgwzu91W7d4W0N/xqEY5MHE+Gd8a0jsHuIOA7E78zeIUiVr8Lc4vLsWmwxfvinmVzL3HW186NZ2FeVsk6luZG+1i3I4GJG/wFX62Quj9SFZsDfMjKX9/1AhtPYIPgblPM0QpiVLINkvtce1aBO7RJu5Hs+VliqRdohw29HxEhBVIKoD+HyEzF4qGCjQkX9CVaX6oYiVtIuD+jDkti1vF9UEPnQqfgRG9R4qQNFzT1sveLsISKFB2sp+iNx85rBZYt1A+iCgtyXtLJ4IRx2i/kKdOSR6nYZcx3hQmb8M6DF35VZztm5a3zWgGHhuTjpfOfscMET+pAvax0jAKTPiR2X/URU1ohTxrKHrzblOmw3oKFgOUr2UJWQfc/61j6pO7fcJD1OhX7K3WNKG6F/RNOko4ZCMOhF2BEwjtVpT/qDngarXgYIJ2+Fb5mgx2WPDXoUvFN5/KV3Nv+S7tIvYNlS7Z4dZkShrqYhVXAZ0qOBZLV/2qmchsOTkQ5lbJN0c0iukPssb0yZILWvTVNsnGx99WwpMTLZppOkue9MxTk9i7kPWiC7oS9e6BbBBRiFDbp14YfjWYUHAwhLDS9otja2IGFgm7PsBaeLzdCDJLkFV2u7epVFynKLfPxNL3bgp2Xb8KZSBf3DS9NiYv518+2sX1b0bYVuzZwEOWpBVH4tXJ2yoy2jhwsc82VWa6KB8Uok3dXZ4rFo8S+Rl+MNhUqW/9ugZfk5dXSJmS5inq2V8SumLOIbadpVMIakgtOOfH4NDD5ckb8eyRik67WxE5ba6IEmowxB+PXIEKvgL06k9ftx2Z23JDtF6O0MTSXZvsPxVS0xoeAxLAAAAAASUVORK5CYII=";
  };

  useEffect(() => {
    if (fullDataPhotos.length != 0) {
      for (var i = 0; i < fullDataPhotos.length + 1; i++) {
        if (i < fullDataPhotos.length) {
          pictureAppend({
            picture: {
              url: fullDataPhotos[i].url,
              id: fullDataPhotos[i].id,
            },
          });
        } else {
          if (fullDataPhotos.length < 5) {
            pictureAppend({
              picture: null,
            });
          }
        }
      }
    }
  }, [fullDataPhotos]);

  useEffect(() => {
    if (!id) {
      pictureAppend({ picture: null });
    }
  }, []);

  function ProductFormatter(cell: any, row: any) {
    return (
      <div className="product-wrapper">
        <div className="product-image">
          <img
            src={row.product.image}
            alt={"image-product"}
            onError={defaultImageSrc}
          />
        </div>
        <Link
          to={`/product/${
            row?.parent_external ? row?.parent_external?.id : row?.external?.id
          }`}
        >
          <div className="product-name">{row.product.name}</div>
        </Link>
      </div>
    );
  }

  const fulfillmentTableColumns = [
    {
      dataField: "product",
      text: t("stock.form.produk.label"),
      formatter: ProductFormatter,
      sort: true,
    },
    {
      dataField: "sku_product",
      text: t("stock.form.sku_produk.label"),
      sort: true,
      classes: "td-custom",
    },
    {
      dataField: "harga",
      text: t("stock.form.harga.label"),
      sort: true,
      classes: "td-custom",
    },
    {
      dataField: "toko",
      text: t("stock.form.channel.label"),
      formatter: (cell: any, row: any) => (
        <img
          className={"channel-icon"}
          src={getChannelConfig(cell)?.simpleLogo}
          onError={defaultImageSrc}
        />
      ),
      sort: true,
      classes: "td-custom",
    },
    {
      dataField: "delete",
      text: "Action",
      formatExtraData: tableData,
      formatter: (cell: any, row: any, rowIndex: any, extraData: any) => (
        <div>
          <button
            type={"button"}
            className={"btn btn-danger btn-small"}
            onClick={() => handleDeleteSkuProduct(row.id, extraData)}
          >
            Delete
          </button>
        </div>
      ),
      sort: false,
      classes: "td-custom",
    },
  ];

  const breadcrumbList = [
    {
      label: t("stock.title"),
      link: "/stock",
    },
    {
      label: id ? t("stock.form.title.edit") : t("stock.form.title.create"),
      link: id ? `/stock/${id}` : "/stock/create",
    },
  ];

  const handleVendorTableSearch = _.debounce(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      tableVendorConfig.search.set(e.target.value);
      tableVendorConfig.page.set(1);

      getVendorTableData();
    },
    300
  );

  function handleAddSkuProduct(id: any) {
    let found = false;
    const dataToAdd = [];
    const prevDataTable = [...tableData];
    for (var j = 0; j < prevDataTable.length; j++) {
      if (prevDataTable[j].id == id) {
        found = true;
        break;
      }
    }

    if (!found) {
      setWarningForm(1);
      for (var i = 0; i < skuProductList.length; i++) {
        if (skuProductList[i].id == id) {
          dataToAdd.push(skuProductList[i]);
          break;
        }
      }
      if (dataToAdd.length != 0) {
        const formatDataToTable = {
          id: dataToAdd[0].id,
          product: {
            name: dataToAdd[0].name,
            image: dataToAdd[0].photo_url,
          },
          sku_product: dataToAdd[0].sku,
          harga: "Default Price",
          toko: dataToAdd[0].channel_id == 1 ? "Tokopedia" : "Shopee",
        };
        setTableData([...tableData, formatDataToTable]);
      }
    } else {
      setWarningForm(2);
    }
  }

  function handleDeleteSkuProduct(id: any, extraData: any) {
    const dataTable = [...tableData];

    for (var i = 0; i < extraData.length; i++) {
      if (extraData[i].id === id) {
        dataTable.splice(i, 1);
        break;
      }
    }

    setTableData(dataTable);
  }

  const onInformationSubmit = informationHandleSubmit((data: any) => {
    const arraySkuProduct = [...tableData];
    const arrayPhoto = data.photo_ids;
    const arrayPhotoIds: any = [];
    const arrayIdSkuProduct: any = [];
    arraySkuProduct.map((skuProduct: any) => {
      arrayIdSkuProduct.push(skuProduct.id);
    });
    for (var j = 0; j < arrayPhoto.length; j++) {
      if (arrayPhoto[j].picture != null) {
        arrayPhotoIds.push(arrayPhoto[j].picture.id);
      }
    }
    const finalData = {
      name: data.name,
      category_id: data.category_id.id,
      sku: data.sku,
      sku_channel_ids: arrayIdSkuProduct,
      photo_ids: arrayPhotoIds,
      vendor_price: data.vendor_price,
    };

    if (id) {
      StokProductRepository.update(brand.id, id, finalData).then((res: any) => {
        setTimeout(function () {
          window.location.assign("/stock");
        }, 1000);
      });
    } else {
      StokProductRepository.create(brand.id, finalData).then((res: any) => {
        setTimeout(function () {
          window.location.assign("/stock");
        }, 1000);
      });
    }
  });

  return (
    <>
      <Helmet>
        <title>{id ? t("Edit Stok") : t("Tambah Stok")}</title>
      </Helmet>

      <VuiBreadcrumb list={breadcrumbList} />

      <ModalUpdateStock
        open={showUpdateStock}
        onClose={handleCloseUpdateStock}
        productId={id}
        callback={getVendorTableData}
      />

      <div className={"page-header-component"}>
        <div className="title-action-wrapper">
          <h3 className={"title"}>
            {id ? t("stock.form.title.edit") : t("stock.form.title.create")}
          </h3>
        </div>
      </div>

      <form onSubmit={onInformationSubmit}>
        <div className={"row"}>
          <div className="col-md-8">
            <div className="card-paper mb-4">
              <div className="card-header">
                <h6 className="card-header-title">
                  {t("stock.form.information.label")}
                </h6>
              </div>

              <div className="card-content">
                <div className="row">
                  <div className="col-12">
                    <div className={"form-group"}>
                      <label className={"form-label"}>
                        {t("stock.form.category.label")}
                      </label>
                      <Controller
                        name="category_id"
                        control={informationControl}
                        defaultValue={null}
                        rules={{ required: true }}
                        render={(props) => (
                          <VuiSelect
                            placeholder="Insert"
                            options={categoryList}
                            defaultValue={props.value}
                            onChange={(value) => {
                              props.onChange(value);
                            }}
                            isDisabled={!loadCategory}
                          />
                        )}
                      />

                      <label className={"label-help"} />
                      {informationErrors.category_id && (
                        <span className={"text-danger"}>
                          Category tidak boleh kosong
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className={"form-group"}>
                      <label className={"form-label"}>
                        {t("stock.form.name.label")}
                      </label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        ref={informationRegister}
                      />
                      <label className={"label-help"} />
                      {informationErrors.name && (
                        <span className={"text-danger"}>
                          Kolom nama tidak boleh kosong
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="col-6">
                    <div className={"form-group"}>
                      <label className={"form-label"}>
                        {t("stock.form.sku_stok.label")}
                      </label>
                      <input
                        type="text"
                        name="sku"
                        className="form-control"
                        ref={informationRegister}
                      />
                      {informationErrors.sku && (
                        <span className={"text-danger"}>
                          Kolom SKU tidak boleh kosong
                        </span>
                      )}
                    </div>
                  </div>

                  {isAdministrator() || isKeyAccount() ? (
                    <div className="col-12">
                      <div className={"form-group"}>
                        <label className={"form-label"}>Harga Vendor</label>
                        <Controller
                          name={"vendor_price"}
                          control={informationControl}
                          defaultValue={""}
                          render={(props) => (
                            <NumberFormat
                              className="form-control"
                              value={props.value}
                              data={props.value}
                              allowNegative={false}
                              thousandSeparator={"."}
                              decimalSeparator={","}
                              decimalScale={0}
                              placeholder={"0"}
                              onValueChange={({ value }) => {
                                props.onChange(value);
                              }}
                            />
                          )}
                        />
                        {informationErrors.vendor_price && (
                          <span className={"text-danger"}>
                            Kolom harga vendor tidak boleh kosong
                          </span>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>

                <label className={"form-label"}>
                  {t("stock.form.picture.label")}
                </label>

                <div className="form-stock-image">
                  {pictureFields.map(renderItemPicture)}
                </div>

                {informationErrors.photo_ids && (
                  <span className={"text-danger"}>
                    Foto Produk tidak boleh kosong
                  </span>
                )}
              </div>
            </div>

            <div className="card-paper mb-4">
              <div className="card-header">
                <h6 className="card-header-title">
                  {t("stock.form.sku_produk.label")}
                </h6>
              </div>

              <div className="card-content">
                <div className="row">
                  <div className="col-12">
                    <div className={"form-group"}>
                      <label className={"form-label"}>
                        {t("stock.form.sku_produk.label")}
                      </label>
                      <div className="row">
                        <div className="col-8 col-md-10">
                          <Controller
                            name="sku_channel_ids"
                            control={informationControl}
                            defaultValue={null}
                            rules={{ required: true }}
                            render={(props) => (
                              <VuiSelect
                                options={skuProductList}
                                defaultValue={props.value}
                                onChange={(value) => {
                                  props.onChange(value);
                                  setSkuProduct(value);
                                }}
                                isDisabled={!loadSKU}
                              />
                            )}
                          />

                          {informationErrors.sku_channel_ids && (
                            <span className={"text-danger"}>
                              Kolom SKU Produk tidak boleh kosong
                            </span>
                          )}
                        </div>
                        <div className="col-4 col-md-2">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() =>
                              handleAddSkuProduct(selectedSkuProduct.id)
                            }
                          >
                            {t("stock.form.button.add")}
                          </button>
                        </div>
                        {warningForm == 2 ? (
                          <div className="col-12 mt-2">
                            <div className="alert alert-warning" role="alert">
                              SKU Produk tersebut sudah digunakan, gunakan SKU
                              Produk yang lain.
                            </div>
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                      <label className={"label-help"}></label>
                    </div>
                  </div>
                </div>

                <BootstrapTable
                  wrapperClasses={"table-responsive"}
                  classes={"table-custom"}
                  keyField={"id"}
                  data={tableData}
                  columns={fulfillmentTableColumns}
                  filter={filterFactory()}
                />
                {tableData.length == 0 ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <div className="ms-3">{t("stock.form.loading.label")}</div>
                  </div>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>

          <div className={"col-md-4"}>
            <div className={"card-paper"}>
              <div className={"card-header"}>
                <h6 className="card-header-title">Keterangan Stok</h6>
                <VuiButton
                  customClass={"btn-sm-not-full-width"}
                  onClick={handleOpenUpdateStock}
                  label={"Update Stock"}
                  loading={false}
                />
              </div>
              <div className={"card-content"}>
                <div className={"default-filter-section"}>
                  <div className="input-group prefix-group">
                    <span className="input-group-text">
                      <FontAwesomeIcon
                        className={"icon icon-prefix"}
                        icon={faSearch}
                      />
                    </span>
                    <input
                      defaultValue={tableVendorConfig.search.get()}
                      type="text"
                      className="form-control"
                      placeholder="Search"
                      onChange={handleVendorTableSearch}
                    />
                  </div>
                </div>
                <VuiDataTable
                  loading={tableVendorConfig.loading.get()}
                  columns={tableVendorColumns}
                  data={$clone(tableVendorData.get())}
                  page={tableVendorConfig.page.get()}
                  sizePerPage={tableVendorConfig.per_page.get()}
                  totalSize={tableVendorConfig.total.get()}
                  onTableChange={onVendorTableChange}
                />
              </div>
            </div>
          </div>
        </div>
      </form>

      {isAdministrator() || isKeyAccount() ? (
        <div className={"card-action-form"}>
          <h6 className={"title"}>{t("stock.form.confirmation.label")}</h6>

          <div className="btn-action-wrapper">
            <Link to={"/stock"} className={"btn btn-sm btn-secondary"}>
              {t("stock.form.button.cancel")}
            </Link>
            <VuiButton
              forSubmit={false}
              label={t("stock.form.button.save")}
              loading={false}
              onClick={onInformationSubmit}
            />
          </div>
        </div>
      ) : null}

      <Modal show={show} onHide={handleClose} animation={false} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("stock.form.modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {id
            ? t("stock.form.modal.pre-body.edit")
            : t("stock.form.modal.pre-body.create")}
          {"."}
          {t("stock.form.modal.body")}{" "}
          {id
            ? t("stock.form.modal.conditional.edit")
            : t("stock.form.modal.conditional.create")}
          .
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleClose}>
            {t("stock.form.modal.confirmation.accept")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StokForm;
